import type { LobbyData, LobbyPlayer, LobbySettings } from "./types";
import type { LobbyQueueType } from "./ranked";
import {
  createLobby as dbCreateLobby,
  joinLobby as dbJoinLobby,
  leaveLobby as dbLeaveLobby,
  setReady as dbSetReady,
  updateLobbyState,
  closeLobby as dbCloseLobby,
  fetchLobby,
  fetchOpenLobbies as dbFetchOpenLobbies,
  fetchLobbyByHost as dbFetchLobbyByHost,
  type VersusDbError
} from "./versusDb";

// ── Lobby ID generation ─────────────────────────────────────────────────────

const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I ambiguity

export function generateLobbyCode(): string {
  let code = "";
  for (let i = 0; i < 6; i++) {
    code += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return code;
}

// ── Default settings ────────────────────────────────────────────────────────

export const DEFAULT_LOBBY_SETTINGS: LobbySettings = {
  startLevel: 1,
  doomClockStartSec: 30,
  trapsEnabled: true
};

interface LobbyErrorMessages {
  fallbackMessage: string;
  networkMessage: string;
}

const CREATE_LOBBY_ERROR_MESSAGES: LobbyErrorMessages = {
  fallbackMessage: "FAILED TO CREATE LOBBY",
  networkMessage: "NETWORK FAILED TO CREATE LOBBY"
};

const JOIN_LOBBY_ERROR_MESSAGES: LobbyErrorMessages = {
  fallbackMessage: "FAILED TO JOIN",
  networkMessage: "NETWORK FAILED TO JOIN LOBBY"
};

const BROWSE_LOBBIES_ERROR_MESSAGES: LobbyErrorMessages = {
  fallbackMessage: "FAILED TO FETCH LOBBIES",
  networkMessage: "NETWORK FAILED TO FIND MATCH"
};

function logVersusDbError(context: string, error: VersusDbError | null): void {
  if (error == null) return;
  console.warn(`[versus] ${context}`, {
    kind: error.kind,
    code: error.code,
    rawMessage: error.rawMessage
  });
}

function mapVersusDbError(
  error: VersusDbError | null,
  messages: LobbyErrorMessages
): string {
  if (error == null) {
    return messages.fallbackMessage;
  }
  if (error.kind === "schema") {
    return "BACKEND UPDATE REQUIRED";
  }
  if (error.kind === "network") {
    return messages.networkMessage;
  }
  return messages.fallbackMessage;
}

// ── Lobby Manager ───────────────────────────────────────────────────────────

export class LobbyManager {
  private currentLobby: LobbyData | null = null;
  private localUserId: string | null = null;
  private localDisplayName = "PLAYER";
  private localElo = 1000;
  private localLevel = 1;

  /** Set the local player identity (call when auth state changes). */
  setLocalPlayer(userId: string, displayName: string, elo: number, level = 1): void {
    this.localUserId = userId;
    this.localDisplayName = displayName;
    this.localElo = elo;
    this.localLevel = Math.max(1, Math.floor(level));
  }

  getCurrentLobby(): LobbyData | null {
    return this.currentLobby;
  }

  getLocalUserId(): string | null {
    return this.localUserId;
  }

  isHost(): boolean {
    return this.currentLobby?.hostId === this.localUserId;
  }

  getLocalPlayer(): LobbyPlayer | null {
    if (this.currentLobby == null || this.localUserId == null) return null;
    return this.currentLobby.players.find((p) => p.userId === this.localUserId) ?? null;
  }

  getOpponent(): LobbyPlayer | null {
    if (this.currentLobby == null || this.localUserId == null) return null;
    return this.currentLobby.players.find((p) => p.userId !== this.localUserId) ?? null;
  }

  // ── Create ──────────────────────────────────────────────────────────────

  async createAndJoinLobby(
    settings: LobbySettings = DEFAULT_LOBBY_SETTINGS,
    queueType: LobbyQueueType = "custom"
  ): Promise<{ lobbyId: string | null; error: string | null }> {
    if (this.localUserId == null) {
      return { lobbyId: null, error: "NOT SIGNED IN" };
    }
    const lobbyId = generateLobbyCode();
    const result = await dbCreateLobby(lobbyId, this.localUserId, settings, queueType);
    if (!result.ok) {
      logVersusDbError("create lobby failed", result.error);
      return {
        lobbyId: null,
        error: mapVersusDbError(result.error, CREATE_LOBBY_ERROR_MESSAGES)
      };
    }
    this.currentLobby = {
      id: lobbyId,
      hostId: this.localUserId,
      queueType,
      state: "WAITING",
      settings,
      matchId: null,
      players: [
        {
          userId: this.localUserId,
          displayName: this.localDisplayName,
          avatarUrl: null,
          ready: false,
          elo: this.localElo,
          equippedSkin: "1",
          level: this.localLevel
        }
      ]
    };
    return { lobbyId, error: null };
  }

  // ── Join ────────────────────────────────────────────────────────────────

  async joinExistingLobby(
    lobbyId: string,
    errorMessages: LobbyErrorMessages = JOIN_LOBBY_ERROR_MESSAGES,
    fallbackQueueType: LobbyQueueType = "custom"
  ): Promise<{ error: string | null }> {
    if (this.localUserId == null) {
      return { error: "NOT SIGNED IN" };
    }
    const lobbyResult = await fetchLobby(lobbyId, fallbackQueueType);
    if (lobbyResult.error != null) {
      logVersusDbError("fetch lobby failed", lobbyResult.error);
      return { error: mapVersusDbError(lobbyResult.error, errorMessages) };
    }
    const lobby = lobbyResult.lobby;
    if (lobby == null) {
      return { error: "LOBBY NOT FOUND" };
    }
    if (lobby.state !== "WAITING") {
      return { error: "LOBBY NOT AVAILABLE" };
    }
    if (lobby.players.length >= 2) {
      return { error: "LOBBY FULL" };
    }
    if (lobby.players.some((p) => p.userId === this.localUserId)) {
      // Already a member — just update local state
      this.currentLobby = lobby;
      return { error: null };
    }
    const joinResult = await dbJoinLobby(lobbyId, this.localUserId);
    if (!joinResult.ok) {
      logVersusDbError("join lobby failed", joinResult.error);
      return { error: mapVersusDbError(joinResult.error, errorMessages) };
    }
    lobby.players.push({
      userId: this.localUserId,
      displayName: this.localDisplayName,
      avatarUrl: null,
      ready: false,
      elo: this.localElo,
      equippedSkin: "1",
      level: this.localLevel
    });
    this.currentLobby = lobby;
    return { error: null };
  }

  // ── Leave ───────────────────────────────────────────────────────────────

  async leaveLobby(): Promise<void> {
    if (this.currentLobby == null || this.localUserId == null) return;
    const lobbyId = this.currentLobby.id;
    const wasHost = this.isHost();
    await dbLeaveLobby(lobbyId, this.localUserId);
    if (wasHost) {
      await dbCloseLobby(lobbyId);
    }
    this.currentLobby = null;
  }

  /** Close the lobby after a match ends. */
  async closeLobby(): Promise<void> {
    if (this.currentLobby == null) return;
    await dbCloseLobby(this.currentLobby.id);
    this.currentLobby = null;
  }

  // ── Ready ───────────────────────────────────────────────────────────────

  async toggleReady(): Promise<void> {
    if (this.currentLobby == null || this.localUserId == null) return;
    const player = this.getLocalPlayer();
    if (player == null) return;
    const newReady = !player.ready;
    const result = await dbSetReady(this.currentLobby.id, this.localUserId, newReady);
    if (result.ok) {
      player.ready = newReady;
      return;
    }
    logVersusDbError("set ready failed", result.error);
  }

  // ── Browse ──────────────────────────────────────────────────────────────

  async fetchOpenLobbies(
    queueType: LobbyQueueType = "custom",
    errorMessages: LobbyErrorMessages = BROWSE_LOBBIES_ERROR_MESSAGES
  ): Promise<{ lobbies: LobbyData[]; error: string | null }> {
    const result = await dbFetchOpenLobbies(queueType);
    if (result.error != null) {
      logVersusDbError("fetch open lobbies failed", result.error);
      return {
        lobbies: [],
        error: mapVersusDbError(result.error, errorMessages)
      };
    }
    return { lobbies: result.lobbies, error: null };
  }

  /** Find an open lobby hosted by a specific user (for friend battles). */
  async fetchLobbyByHost(hostUserId: string, queueType: LobbyQueueType = "custom"): Promise<LobbyData | null> {
    return dbFetchLobbyByHost(hostUserId, queueType);
  }

  // ── Refresh ─────────────────────────────────────────────────────────────

  async refreshLobby(): Promise<void> {
    if (this.currentLobby == null) return;
    const result = await fetchLobby(this.currentLobby.id, this.currentLobby.queueType);
    if (result.error != null) {
      logVersusDbError("refresh lobby failed", result.error);
      return;
    }
    if (result.lobby != null) {
      this.currentLobby = result.lobby;
    }
  }

  // ── State transitions ─────────────────────────────────────────────────

  async setLobbyState(state: LobbyData["state"], matchId?: string): Promise<boolean> {
    if (this.currentLobby == null) return false;
    const result = await updateLobbyState(this.currentLobby.id, state, matchId);
    if (result.ok) {
      this.currentLobby.state = state;
      if (matchId != null) {
        this.currentLobby.matchId = matchId;
      }
      return true;
    }
    logVersusDbError("set lobby state failed", result.error);
    return false;
  }

  /** Clear local lobby reference (e.g. after match ends). */
  clearLobby(): void {
    this.currentLobby = null;
  }
}
